import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, TrendingUp, PackagePlus, ClipboardCheck, Truck, LifeBuoy,
  ShieldCheck, Megaphone, Warehouse, IndianRupee, Clock, Users, Store,
} from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { V1 } from "@/constants/testIds";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

function KPI({ label, value, hint, icon: Icon, tone, testId, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="text-left rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:shadow transition">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon size={14} className={tone || "text-slate-400"} />}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </button>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/dashboard");
      setD(r.data);
    } catch (e) {
      setErr(errorToMessage(e, "Failed to load dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid={V1.dashPage}>
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Executive Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Snapshot across sales, procurement, QC, inventory, warranty and campaigns.
            {d?.generated_at && ` · Refreshed ${new Date(d.generated_at).toLocaleTimeString()}`}
          </p>
        </div>
        <button type="button" onClick={load} data-testid={V1.dashRefresh}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>
      )}
      {loading && !d && <div className="text-slate-400 text-sm">Loading…</div>}

      {d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <KPI label="Today's Sales" value={inr(d.sales.today_sales)}
              hint={`${d.sales.today_orders} order(s)`}
              icon={TrendingUp} tone="text-emerald-700"
              testId={V1.cardTodaySales} onClick={() => navigate("/admin/sales")} />
            <KPI label="MTD Sales" value={inr(d.sales.mtd_sales)}
              hint={`${d.sales.mtd_orders} orders`}
              icon={TrendingUp} testId={V1.cardMTDSales}
              onClick={() => navigate("/admin/sales")} />
            <KPI label="Pending Procurement" value={d.procurement.pending}
              hint={`${inr(d.procurement.pending_value)} value`}
              icon={PackagePlus} testId={V1.cardPendingProcure}
              onClick={() => navigate("/admin/purchases")} />
            <KPI label="QC Pending" value={d.qc.pending}
              hint={`${d.qc.in_progress} in service`}
              icon={ClipboardCheck} testId={V1.cardPendingQC}
              onClick={() => navigate("/admin/qc/jobs")} />
            <KPI label="Pending Dispatch" value={d.sales.pending_dispatch}
              hint="Confirmed orders" icon={Truck}
              testId={V1.cardPendingDispatch}
              onClick={() => navigate("/admin/sales")} />
            <KPI label="Open Cases" value={d.cases.open_cases}
              hint={`${d.cases.urgent} urgent · ${d.cases.new_7d} new 7d`}
              icon={LifeBuoy} tone="text-rose-700"
              testId={V1.cardOpenCases} onClick={() => navigate("/admin/cases")} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <KPI label="Warranty Due (30d)" value={d.warranty.due_30d}
              hint={`${d.warranty.due_7d} within 7d`}
              icon={ShieldCheck} tone="text-amber-700"
              testId={V1.cardWarrantyDue}
              onClick={() => navigate("/admin/warranties")} />
            <KPI label="Campaigns Running" value={d.campaign.running}
              hint={`${d.campaign.renewed} renewed · ${inr(d.campaign.revenue)}`}
              icon={Megaphone} tone="text-indigo-700"
              testId={V1.cardCampaignRunning}
              onClick={() => navigate("/admin/campaigns")} />
            <KPI label="Inventory Value" value={inr(d.inventory.on_hand_value)}
              hint={`${d.inventory.available} avail · ${d.inventory.reserved} reserved`}
              icon={IndianRupee} testId={V1.cardInventoryValue}
              onClick={() => navigate("/admin/inventory")} />
            <KPI label="Dead Stock (90d+)" value={d.inventory.dead_stock}
              hint="Not moved in 90 days"
              icon={Warehouse} tone="text-rose-600"
              testId={V1.cardDeadStock}
              onClick={() => navigate("/admin/inventory")} />
            <KPI label="Sold Units" value={d.inventory.sold} icon={Truck}
              onClick={() => navigate("/admin/inventory")} />
            <KPI label="Warranties Active" value={d.warranty.active}
              icon={ShieldCheck} tone="text-emerald-700"
              onClick={() => navigate("/admin/warranties")} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ListPanel title="Top Customers" icon={Users} testId={V1.topCustomers}
              rows={d.top_customers}
              cols={["Customer", "Orders", "Total"]}
              render={(r) => [r.full_name, r.orders, inr(r.total_spent)]} />
            <ListPanel title="Top Vendors" icon={Store} testId={V1.topVendors}
              rows={d.top_vendors}
              cols={["Vendor", "POs", "Total"]}
              render={(r) => [r.name, r.orders, inr(r.total_spent)]} />
            <ListPanel title="Fast Moving (90d)" icon={TrendingUp} testId={V1.fastMoving}
              rows={d.fast_moving}
              cols={["Brand · Model", "Units", "Revenue"]}
              render={(r) => [`${r.brand || ""} ${r.model || ""}`.trim() || "—",
                              r.units_sold, inr(r.revenue)]} />
          </div>

          <div className="rounded-lg border border-slate-200 bg-white shadow-sm"
            data-testid={V1.recentActivity}>
            <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold flex items-center gap-2">
              <Clock size={14} /> Recent Activity
            </div>
            <ul className="divide-y divide-slate-100">
              {d.recent_activity.length === 0 && (
                <li className="px-4 py-6 text-sm text-slate-400">No recent activity.</li>
              )}
              {d.recent_activity.map((a, i) => (
                <li key={`${a.entity}-${a.entity_id}-${a.at}-${i}`}
                  className="px-4 py-2.5 text-sm flex items-baseline justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs text-slate-500">{a.entity}</span>{" "}
                    <span className="text-slate-700">{a.action}</span>
                  </div>
                  <div className="text-xs text-slate-500 whitespace-nowrap">
                    {a.by} · {a.at ? new Date(a.at).toLocaleString() : "—"}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function ListPanel({ title, icon: Icon, rows, cols, render, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold flex items-center gap-2">
        {Icon && <Icon size={14} />} {title}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>{cols.map((c) => <th key={c} className="text-left px-4 py-2">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-slate-400 text-sm">No data yet.</td></tr>
          )}
          {rows.map((r, i) => {
            const cells = render(r);
            return (
              <tr key={r.id || r.name || i} className="border-t border-slate-100">
                {cells.map((c, ci) => <td key={ci} className="px-4 py-2 text-xs">{c}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
